import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { unstable_createMuiStrictModeTheme as createMuiTheme } from '@material-ui/core/styles';
import { MuiThemeProvider, StylesProvider } from '@material-ui/core/styles';
import { itIT } from '@mui/x-data-grid';
import * as Sentry from '@sentry/react';
import ErrorBoundaryFallback from 'components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider } from 'styled-components';

import CustomLoader from './components/CustomLoader/CustomLoader';
import ServerErrorMessage from './components/ServerErrorMessage/ServerErrorMessage';
import PageContainer from './layout/PageContainer';
import { setFromApi as setLanguagesFromApi } from './redux/languages/languagesSlice';
import { setFromApi } from './redux/user/userSlice';
import api from './utils/api';
import DialogProvider from './utils/dialog';
import i18nInit from './utils/i18nInit';
import { SnackbarUtilsConfigurator } from './utils/snackbar';

/*
 * application entry point
 */

const App = () => {
    const [apiRequestDone, setApiRequestDone] = useState(false);
    const [internationalizationLoaded, setInternationalizationLoaded] = useState(false);
    const [connectionError, setConnectionError] = useState(false);

    const theme = createMuiTheme({
        palette: {
            primary: {
                main: '#194560',
            }
        },
    }, itIT);

    const dispatch = useDispatch();

    useEffect(() => {
        // check if the user is logged in
        api.isLoggedIn().then(isLoggedIn => {
            if (isLoggedIn) {
                // if the user is logged in, retrieves his profile info and saves it in the Redux state
                api.request('/users/me').then(profile => {
                    dispatch(setFromApi(profile));
                    setApiRequestDone(true);
                }).catch(() => {
                    // if the API request fails, display the error message
                    setConnectionError(true);
                });
            }
            else {
                setApiRequestDone(true);
            }
        }).catch(() => {
            // if the API request fails, display the error message
            setConnectionError(true);
        });

        api.request('/languages').then(res => {
            dispatch(setLanguagesFromApi(res));
        });

        i18nInit((err: any) => {
            if (err) {
                // if the i18n init fails, display the error message
                setConnectionError(true);
            }
            setInternationalizationLoaded(true);
        });
    }, [dispatch]);

    if (connectionError) {
        return (
            <ServerErrorMessage />
        );
    }

    if (!apiRequestDone || !internationalizationLoaded) {
        // show the loader if requests to the API have not yet been completed
        return (
            <CustomLoader />
        );
    }

    // various providers and components are loaded
    return (
        // @ts-ignore
        <Sentry.ErrorBoundary fallback={ErrorBoundaryFallback}>
            <StylesProvider injectFirst>
                <MuiThemeProvider theme={theme}>
                    <ThemeProvider theme={theme}>
                        {/* // @ts-ignore */}
                        <SnackbarProvider anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }} autoHideDuration={5000} maxSnack={3}>
                            <SnackbarUtilsConfigurator />
                            <DialogProvider>
                                <BrowserRouter basename={process.env.PUBLIC_URL ? (new URL(process.env.PUBLIC_URL).pathname) : '/'}>
                                    <PageContainer />
                                </BrowserRouter>
                            </DialogProvider>
                        </SnackbarProvider>
                    </ThemeProvider>
                </MuiThemeProvider>
            </StylesProvider>
        </Sentry.ErrorBoundary>
    );
};

export default App;
